import { createApiClient, ApiError } from "@wd/shared";
import { emitToast } from "./Toast";

// Access token kept in memory; refresh token is an httpOnly cookie handled by the API.
let accessToken: string | null = null;
export function setAccessToken(t: string | null) {
  accessToken = t;
}
export function getAccessToken() {
  return accessToken;
}

const BASE = "/api";

const client = createApiClient({
  baseUrl: BASE,
  getToken: () => accessToken,
});

// Access tokens are short-lived (15 min). Rather than failing any request that
// outlives the token with "Invalid or expired token", transparently refresh once
// (using the httpOnly refresh cookie) and retry. Concurrent 401s share a single
// in-flight refresh so we don't fire N refreshes at once.
let refreshing: Promise<boolean> | null = null;
function refreshSession(): Promise<boolean> {
  if (!refreshing) {
    refreshing = bootstrapSession().finally(() => {
      refreshing = null;
    });
  }
  return refreshing;
}

function withRefresh<A extends unknown[], T>(
  fn: (...args: A) => Promise<T>,
): (...args: A) => Promise<T> {
  return async (...args: A) => {
    try {
      return await fn(...args);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401 && (await refreshSession())) {
        return await fn(...args); // retry once with the refreshed token
      }
      throw err;
    }
  };
}

// The shared client only exposes `onUnauthorized` (no general error hook), so we
// wrap each method to surface non-401 ApiErrors as toasts and then re-throw so
// callers still receive the original error. A genuine 401 (refresh also failed)
// is left to the auth flow to handle (redirect to login) without a duplicate toast.
function withToast<A extends unknown[], T>(
  fn: (...args: A) => Promise<T>,
): (...args: A) => Promise<T> {
  return async (...args: A) => {
    try {
      return await fn(...args);
    } catch (err) {
      if (err instanceof ApiError && err.status !== 401) {
        emitToast(err.message, "error");
      }
      throw err;
    }
  };
}

// Compose: toast(refresh(rawCall)) — refresh+retry happens first; if it still
// fails, toast decides whether to surface it.
export const api = {
  get: withToast(withRefresh(client.get)),
  post: withToast(withRefresh(client.post)),
  patch: withToast(withRefresh(client.patch)),
  del: withToast(withRefresh(client.del)),
  raw: withToast(withRefresh(client.raw)),
};

// Try to refresh the session on app load (uses the refresh cookie).
export async function bootstrapSession(): Promise<boolean> {
  try {
    const res = await fetch(`${BASE}/auth/refresh`, { method: "POST", credentials: "include" });
    if (!res.ok) return false;
    const data = (await res.json()) as { accessToken: string };
    accessToken = data.accessToken;
    return true;
  } catch {
    return false;
  }
}
